import * as yup from 'yup'

export const minimumRequirementType = {
  NONE: 'NONE',
  PURCHASE_AMOUNT: 'PURCHASE_AMOUNT',
  QUANTITY: 'QUANTITY',
}

export const discountType = {
  PRODUCT: 'PRODUCT',
  ORDER: 'ORDER',
  SHIPPING: 'SHIPPING',
  WINE_TASTING: 'WINE_TASTING',
}

export const discountValueType = {
  PERCENTAGE: 'PERCENTAGE',
  FIXED_AMOUNT: 'FIXED_AMOUNT',
  SHIPPING: 'SHIPPING',
}

export const discountCustomerType = {
  ALL_CUSTOMERS: 'ALL',
  WINE_CLUB: 'WINE_CLUB_MEMBER',
}

export const discountWineClubType = {
  ALL_WINE_CLUB_MEMBER: 'ALL',
  SPECIFIC_WINE_CLUB: 'SPECIFIC_WINE_CLUB',
  ALL_CUSTOMERS: 'ALL_CUSTOMERS',
}

export const discountMethodType = {
  Discount_code: 'Discount code',
  Automatic_discount: 'Automatic discount',
  discount_method: 'Discount method',
}

export const discountApplies: any = {
  PRODUCT: 'PRODUCT',
  COLLECTION: 'COLLECTION',
}

export const discountTargetType = {
  PRODUCT: 'PRODUCT',
  SHIPPING: 'SHIPPING',
}

export const discountOfferTypes = [
  {
    id: 'ORDER',
    name: 'All Orders (E-Com orders, Wine club orders)',
  },
  {
    id: 'PRODUCT',
    name: 'Product',
  },
  {
    id: 'SHIPPING',
    name: 'Shipping',
  },
  {
    id: 'WINE_TASTING',
    name: 'Wine tasting',
  },
]

export const discountSpecificType = {
  PRODUCT: 'PRODUCT',
  COLLECTION: 'COLLECTION',
}

export const discountTypesName = {
  [discountType.PRODUCT]: 'Product',
  [discountType.ORDER]: 'Order',
  [discountType.SHIPPING]: 'Shipping',
  [discountType.WINE_TASTING]: 'Wine Tasting',
}

export const getDiscountNameByType = (type: string) => {
  return discountTypesName[type]
}

export type DiscountRule = {
  id?: string
  discountType?: string
  discountValueType?: string
  targetType: string
  valueType: string
  discountValue: number | ''
  minimumRequirementType: string | ''
  prerequisiteSubtotal: number | ''
  prerequisiteShippingPrice?: number | ''
  prerequisiteQuantity: number | ''
  startsAt: any
  endsAt: any
  isExcludeShippingPrice?: boolean
  isLimitedUse?: boolean
  usageLimit: number | ''
  oncePerCustomer: boolean
  maximumAmount: string | ''
  discountSpecificType: string
  discountProducts: any[]
  discountCollections: any[]
  noEndDate?: boolean
  discountSpecificCustomerType: string | ''
  discountSpecificWineClubType: string | ''
  discountWineClubs: any[]
  combinesWithProductDiscount: boolean
  combinesWithOrderDiscount: boolean
  combinesWithShippingDiscount: boolean
}
export type DiscountCode = {
  id?: string
  status?: string
  code: string
  name: string
  discountRule: DiscountRule
  discountCodeAssociations: any[]
}

export const discountCodeInitialState: DiscountCode = {
  code: '',
  name: '',
  discountCodeAssociations: [],
  discountRule: {
    discountType: discountType.ORDER,
    targetType: discountTargetType.PRODUCT,
    valueType: discountValueType.PERCENTAGE,
    discountValueType: discountValueType.PERCENTAGE,
    discountValue: '',
    minimumRequirementType: minimumRequirementType.NONE,
    prerequisiteSubtotal: '',
    isExcludeShippingPrice: false,
    prerequisiteShippingPrice: '',
    prerequisiteQuantity: '',
    startsAt: '',
    endsAt: '',
    isLimitedUse: false,
    usageLimit: '',
    oncePerCustomer: false,
    maximumAmount: '',
    discountSpecificType: '',
    discountProducts: [],
    discountCollections: [],
    noEndDate: false,
    discountSpecificCustomerType: discountCustomerType.ALL_CUSTOMERS,
    discountSpecificWineClubType: '',
    discountWineClubs: [],
    combinesWithProductDiscount: false,
    combinesWithOrderDiscount: false,
    combinesWithShippingDiscount: false,
  },
}

export const DiscountRuleSchema = yup.object().shape({
  targetType: yup.string().trim().required('Target type is required'),
  valueType: yup.string().trim().required('Value type is required'),
  discountValue: yup.number().when('discountType', {
    is: (value: any) => value !== discountValueType.SHIPPING,
    then: yup.number().required('Discount value is required'),
  }),
  prerequisiteSubtotal: yup.number().when('minimumRequirementType', {
    is: minimumRequirementType.PURCHASE_AMOUNT,
    then: yup.number().required('Purchase amount is required'),
    otherwise: yup.number().notRequired(),
  }),

  prerequisiteQuantity: yup.number().when('minimumRequirementType', {
    is: minimumRequirementType.QUANTITY,
    then: yup.number().required('Item quantity is required'),
    otherwise: yup.number().notRequired(),
  }),

  usageLimit: yup.number().when('isLimitedUse', {
    is: true,
    then: yup.number().required('Usage limit is required'),
    otherwise: yup.number().notRequired(),
  }),

  prerequisiteShippingPrice: yup.number().when('isExcludeShippingPrice', {
    is: true,
    then: yup.number().required('Shipping rate amount is required'),
    otherwise: yup.number().notRequired(),
  }),

  startsAt: yup.string().trim().required('Start date is required'),
  endsAt: yup.string().when('noEndDate', {
    is: false,
    then: yup.string().trim().required('End date is required').nullable(),
    otherwise: yup.string().notRequired().nullable(),
  }),
  discountSpecificType: yup.string().when('discountType', {
    is: discountType.PRODUCT,
    then: yup.string().trim().required('Please select a product or collection'),
  }),
  discountCollections: yup.array().when('discountSpecificType', {
    is: discountApplies.COLLECTION,
    then: yup
      .array()
      .required('Please select a collection')
      .min(1, 'Please select at least one collection'),
    otherwise: yup.array().notRequired().nullable(),
  }),
  discountProducts: yup.array().when('discountSpecificType', {
    is: discountApplies.PRODUCT,
    then: yup.array().min(1, 'Please select at least one products'),
    otherwise: yup.array().notRequired().nullable(),
  }),
  discountSpecificWineClubType: yup.string().when('discountSpecificCustomerType', {
    is: discountCustomerType.WINE_CLUB,
    then: yup.string().required('Please select an wine club type').nullable(),
    otherwise: yup.string().notRequired().nullable(),
  }),
  discountWineClubs: yup.array().when('discountSpecificWineClubType', {
    is: discountWineClubType.SPECIFIC_WINE_CLUB,
    then: yup.array().min(1, 'Please select at least one wine club'),
    otherwise: yup.array().notRequired().nullable(),
  }),
})

export const DiscountCodeSchema = yup.object().shape({
  code: yup.string().trim().required('Code is required'),
  discountRule: DiscountRuleSchema,
})
